import { clsx } from "clsx";
import { type FC, useMemo } from "react";

import { Icons } from "widgets";
import { extensionToColor, getFilenameInfo } from "widgets/utils";

import style from "@/modules/core/styles/Tile.module.scss";

import { FileTypeIcons } from "@/settings/assets-settings.js";

interface FileIconProps {
  /** If not given, it renders an empty file icon */
  filename?: string | null;
  /** Default: "big" */
  size?: "big" | "small";
  /** Use the file stack instead of the usual file icon */
  asStack?: boolean;
  /** Use the directory icon */
  isFolder?: boolean;
}

/**
 * Without any props, it will render the default empty file icon
 */
export const FileIcon: FC<FileIconProps> = (props) => {
  const [width, height, smallHeight] = useMemo(() => {
    const height = props.size === "small" ? 24 : 30;
    const width = height === 30 ? 26 : 18;
    const smallHeight = (height / 2 - 2).toFixed();

    return [width, height, smallHeight];
  }, [props.size]);

  const [extension, extensionInfo] = useMemo(() => {
    const fileInfo = getFilenameInfo(props.filename);
    return [fileInfo?.extension, fileInfo?.extensionInfo];
  }, [props.filename]);

  // A directory has no file extension, but extension is set to "unknown" because files may lack the extension as well.
  // Therefore we handle the directory case right away, as the first one.
  if (props.isFolder) {
    return <img src={Icons.Tiles.FileBrowser} width={width} height={height} style={{ display: "inline-block" }} />;
  }

  if (extension != undefined) {
    // may be undefined if "ext" doesn't exist in the FileTypeIcons list
    const Icon = FileTypeIcons[extension.toLowerCase()];
    const background = extensionToColor(extension);

    return (
      <div className={style.file_with_icon} style={{ height }}>
        {Icon ? (
          <Icon
            height={smallHeight}
            width={smallHeight}
            className={clsx(style.file_icon, { [style.file_icon_stack]: props.asStack === true })}
          />
        ) : extensionInfo?.isScientific ? (
          <img
            src={Icons.Tiles.Entity}
            height={smallHeight}
            width={smallHeight}
            className={clsx(style.file_icon_ext, { [style.file_icon_stack]: props.asStack === true })}
          />
        ) : extensionInfo?.isTechnical ? (
          <img
            src={Icons.Tiles.Technical}
            height={smallHeight}
            width={smallHeight}
            className={clsx(style.file_icon_ext, { [style.file_icon_stack]: props.asStack === true })}
          />
        ) : null}

        {!Icon && extension && (
          <div className={style.file_ext} style={{ background }}>
            {extension.substr(0, props.size === "small" ? 3 : 5)}
          </div>
        )}
        {props.asStack === true ? (
          <img src={Icons.Tiles.FilesStack} width={width} height={height} />
        ) : (
          <img src={Icons.Tiles.File} width={width} height={height} />
        )}
      </div>
    );
  }

  return props.asStack === true ? (
    <img src={Icons.Tiles.FilesStack} width={width} height={height} style={{ display: "inline-block" }} />
  ) : (
    <img src={Icons.Tiles.File} width={width} height={height} style={{ display: "inline-block" }} />
  );
};
